import { useCallback, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { AppMenuItem } from '@/UI/AppContextMenu/AppMenu.styles';
import { TextIteratorIcon } from '@/UI/Icons/TextIteratorIcon';
import { EMPTY_VALUE_COLOR, EMPTY_VALUE_TEXT, SOURCE_COLOR, VALUE_COLOR } from './constants';
import { getTagDisplay } from './utils';
import type { AvailableInput } from './types';

interface MentionMenuItemProps {
  input: AvailableInput;
  onInsertInputTag: (input: AvailableInput) => void;
  setRef: (key: string, element: HTMLLIElement | null) => void;
}

export const MentionMenuItem = ({ input, onInsertInputTag, setRef }: MentionMenuItemProps) => {
  const { label, value, isIterator } = getTagDisplay(input, 'source-value');

  const handleClick = useCallback(() => {
    onInsertInputTag(input);
  }, [input, onInsertInputTag]);

  const refCallback = useCallback(
    (element: HTMLLIElement | null) => {
      setRef(input.key, element);
    },
    [input.key, setRef],
  );

  // Cleanup ref when unmounting
  useEffect(() => {
    return () => {
      setRef(input.key, null);
    };
  }, [input.key, setRef]);

  return (
    <AppMenuItem ref={refCallback} onClick={handleClick}>
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          overflow: 'hidden',
        }}
      >
        {isIterator && (
          <TextIteratorIcon
            style={{
              width: 12,
              height: 12,
              marginRight: 4,
              flexShrink: 0,
              color: SOURCE_COLOR,
            }}
          />
        )}
        <Box
          component="span"
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            minWidth: 0,
          }}
        >
          <Typography fontSize="inherit" color={SOURCE_COLOR} component="span">
            {label}
          </Typography>
          <Typography
            fontSize="inherit"
            color={value === EMPTY_VALUE_TEXT ? EMPTY_VALUE_COLOR : VALUE_COLOR}
            component="span"
          >
            {value}
          </Typography>
        </Box>
      </Box>
    </AppMenuItem>
  );
};
